import React, { useState, useEffect, useCallback } from 'react';
import './CursosList.css';
import { apiFetch } from '../../../services/dashboardApi';
import DashboardButton from './ui/DashboardButton';

function CursosList({ token, canEdit = false }) {
    const [cursos, setCursos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingCurso, setEditingCurso] = useState(null);

    const fetchCursos = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiFetch('http://localhost:8000/api/cursos/', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setCursos(data.results || data || []);
        } catch (err) {
            console.error('Error al cargar cursos:', err);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchCursos();
    }, [fetchCursos]);

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar este curso?')) return;
        try {
            const response = await apiFetch(`http://localhost:8000/api/cursos/${id}/`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                alert('Curso eliminado');
                fetchCursos();
            }
        } catch (err) {
            alert('Error: ' + err.message);
        }
    };

    if (showForm) {
        return (
            <CursoForm
                curso={editingCurso}
                token={token}
                onCancel={() => { setShowForm(false); setEditingCurso(null); fetchCursos(); }}
            />
        );
    }

    return (
        <div className="cursos-list__container">
            <h3>Gestión de Cursos</h3>
            {canEdit && (
                <DashboardButton variant="success" onClick={() => { setEditingCurso(null); setShowForm(true); }}>
                    Crear Nuevo Curso
                </DashboardButton>
            )}

            {loading && <p className="muted">Cargando cursos...</p>}

            {!loading && cursos.length === 0 && <p className="muted">No hay cursos publicados.</p>}

            {!loading && Array.isArray(cursos) && cursos.map(curso => (
                <div key={curso.id} className="curso-card">
                    <h4>{curso.titulo}</h4>
                    <p><strong>Categoría:</strong> {curso.categoria || 'N/A'}</p>
                    <p><strong>Fecha de Publicación:</strong> {curso.fpublicacion}</p>
                    <p>{curso.descripcion?.substring(0, 200)}...</p>
                    {curso.portada && <img src={curso.portada} alt={curso.titulo} className="curso-portada" />}
                    {canEdit && (
                        <div className="curso-actions">
                            <DashboardButton variant="warning" onClick={() => { setEditingCurso(curso); setShowForm(true); }}>Editar</DashboardButton>
                            <DashboardButton variant="danger" onClick={() => handleDelete(curso.id)}>Eliminar</DashboardButton>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

function CursoForm({ curso, token, onCancel }) {
    const [formData, setFormData] = useState({
        titulo: '',
        categoria: '',
        descripcion: '',
        fpublicacion: new Date().toISOString().split('T')[0]
    });
    const [portada, setPortada] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (curso) {
            setFormData({
                titulo: curso.titulo || '',
                categoria: curso.categoria || '',
                descripcion: curso.descripcion || '',
                fpublicacion: curso.fpublicacion || new Date().toISOString().split('T')[0]
            });
        }
    }, [curso]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handlePortadaChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setPortada(e.target.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const url = curso
                ? `http://localhost:8000/api/cursos/${curso.id}/`
                : 'http://localhost:8000/api/cursos/';
            const method = curso ? 'PATCH' : 'POST';

            // Usar FormData para enviar archivos
            const data = new FormData();
            data.append('titulo', formData.titulo);
            data.append('categoria', formData.categoria);
            data.append('descripcion', formData.descripcion);
            data.append('fpublicacion', formData.fpublicacion);
            if (portada) {
                data.append('portada', portada);
            }

            const response = await apiFetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`
                    // No incluir Content-Type, el navegador lo configurará automáticamente
                },
                body: data
            });

            if (response.ok) {
                alert(curso ? 'Curso actualizado' : 'Curso creado');
                onCancel();
            } else {
                const responseData = await response.json();
                alert('Error: ' + JSON.stringify(responseData));
            }
        } catch (err) {
            alert('Error: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="curso-form__container">
            <h3>{curso ? 'Editar Curso' : 'Crear Nuevo Curso'}</h3>
            <form onSubmit={handleSubmit} className="curso-form">
                <div className="form-group">
                    <label>Título:*</label>
                    <input
                        type="text"
                        name="titulo"
                        value={formData.titulo}
                        onChange={handleChange}
                        required
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Categoría:</label>
                    <input
                        type="text"
                        name="categoria"
                        value={formData.categoria}
                        onChange={handleChange}
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Descripción:*</label>
                    <textarea
                        name="descripcion"
                        value={formData.descripcion}
                        onChange={handleChange}
                        required
                        rows="10"
                        className="form-textarea"
                    />
                </div>

                <div className="form-group">
                    <label>Fecha de Publicación:</label>
                    <input
                        type="date"
                        name="fpublicacion"
                        value={formData.fpublicacion}
                        onChange={handleChange}
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Portada:</label>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handlePortadaChange}
                        className="file-input"
                    />
                    {curso && curso.portada && (
                        <div className="current-portada">
                            <p>Portada actual:</p>
                            <img src={curso.portada} alt="Preview" className="curso-portada" />
                        </div>
                    )}
                    {portada && (
                        <p className="success-text">Nueva portada seleccionada: {portada.name}</p>
                    )}
                </div>

                <div className="form-actions">
                    <DashboardButton type="submit" disabled={loading} variant="success" style={{ marginRight: '10px' }}>
                        {loading ? 'Guardando...' : (curso ? 'Actualizar' : 'Crear')}
                    </DashboardButton>
                    <DashboardButton type="button" onClick={onCancel} variant="secondary">Cancelar</DashboardButton>
                </div>
            </form>
        </div>
    );
}

export default CursosList;
